/**
 * Between the document a writer sees and the Markdown the system stores.
 *
 * The people using this are clinic staff, not developers. They must never meet a
 * hash, a pipe table or an angle bracket - so the editor shows a finished
 * document and nothing else. Everything downstream, though, reads Markdown: the
 * exports (text / Markdown / HTML / WordPress), the legal check, the editorial
 * checks, the outline rail's heading matching and the per-section drafting all
 * parse it. So Markdown stays the stored truth and this module is the only place
 * the two forms meet.
 *
 * WHY NOT STORE HTML. It would make the editor simpler and everything else
 * harder: four exports, five checks and the section splitter would each need
 * rewriting against HTML, and the WordPress-Markdown path the requirements ask
 * for would become lossy. One conversion at the edge is cheaper than five
 * rewrites in the middle.
 *
 * ROUND TRIPPING IS THE WHOLE PROBLEM. Anything the editor can produce must
 * survive being written to Markdown and read back, or a writer loses work by
 * saving. Most of the toolbar is what GFM already carries - headings, bold,
 * italic, strike, lists, links, tables, quotes, rules, images. The rest -
 * the four coloured boxes, a checklist, a text colour, a text size - has no
 * Markdown at all, so each is written as literal HTML and passed through
 * untouched in both directions. See the rules below: every control in the
 * toolbar is answered by one of them.
 */
import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

/**
 * Markdown to HTML, for putting a stored article into the editor.
 *
 * `html: true` because the two callout boxes are stored as literal HTML and have
 * to come back as elements rather than as visible tags. That is safe here in a
 * way it would not be on a public page: this HTML is the clinic's own article,
 * rendered inside their own editor, and it never leaves this application without
 * passing through the export path.
 */
const md = new MarkdownIt({ html: true, linkify: true, breaks: false });

/**
 * HTML back to Markdown, for saving.
 *
 * ATX headings and fenced code because that is what the rest of the system
 * parses; `_` for emphasis avoids the `*` collisions that make a Japanese
 * sentence with a wildcard in it round-trip badly.
 */
const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
  emDelimiter: '_',
});
// Tables, strikethrough and task lists: GFM is what the exports already emit.
turndown.use(gfm);

/*
 * The marker pen survives as itself: `<mark>` has no Markdown, and a clinic
 * highlights a price or a caveat constantly.
 */
turndown.keep(['mark']);

/*
 * Typed structurally rather than as an HTMLElement: this module is also loaded
 * by its unit test, which runs under the server's TypeScript config where there
 * is no DOM. All that is needed of the node is its name, its class and its own
 * markup.
 */
interface Htmlish {
  nodeName: string;
  getAttribute?: (name: string) => string | null;
  outerHTML?: string;
}

/**
 * The structured blocks, kept verbatim.
 *
 * A coloured box, a supervisor credit and a CTA are all the same shape of thing:
 * a div with a known class holding ordinary block content, none of which
 * Markdown can express. Each is stored as its own HTML and passed straight back
 * through, which is what makes them survive a save - and each is rebuilt from
 * scratch against this same allowlist on the way out to a published file (see
 * server/lib/markdown.ts).
 */
const DOC_BLOCKS = ['callout', 'bio', 'ctabox'];

turndown.addRule('docBlock', {
  filter: (node: Htmlish) => {
    if (node.nodeName !== 'DIV') return false;
    const classes = (node.getAttribute?.('class') ?? '').split(/\s+/);
    return DOC_BLOCKS.some((name) => classes.includes(name));
  },
  replacement: (_content: string, node: Htmlish) => `\n\n${node.outerHTML ?? ''}\n\n`,
});

/*
 * A checklist survives as itself.
 *
 * GFM writes one as `- [ ] `, but reading it back needs a Markdown parser that
 * understands the syntax, and markdown-it does not without a plugin. Kept as its
 * own HTML instead - which the editor parses back into a real checklist - so a
 * ticked box is still a ticked box after a save.
 */
turndown.addRule('taskList', {
  filter: (node: Htmlish) =>
    node.nodeName === 'UL' && node.getAttribute?.('data-type') === 'taskList',
  replacement: (_content: string, node: Htmlish) => `\n\n${node.outerHTML ?? ''}\n\n`,
});

/*
 * A colour and a size survive as themselves.
 *
 * Markdown has no notion of either and the toolbar offers both, so the span the
 * editor produces is written out verbatim and read straight back. Inline, with
 * no surrounding blank lines, because it sits inside a sentence.
 *
 * Only spans carrying a style: an unstyled one is noise from a paste, and its
 * text is all that matters.
 */
turndown.addRule('styledSpan', {
  filter: (node: Htmlish) =>
    node.nodeName === 'SPAN' && (node.getAttribute?.('style') ?? '') !== '',
  replacement: (_content: string, node: Htmlish) => node.outerHTML ?? '',
});

/** A stored article, as a document the editor can show. */
export const toHtml = (markdown: string): string => md.render(markdown ?? '');

/**
 * A replacement for part of a paragraph, as inline HTML - or null when it is
 * more than one paragraph and has to go in as blocks.
 *
 * `toHtml` wraps everything in <p>, and a <p> inserted over a sentence in the
 * middle of a paragraph splits that paragraph in two: AIで直す put the fixed
 * sentence on a line of its own, breaking the paragraph it was meant to sit
 * inside. One paragraph of prose renders inline instead, keeping its bold and
 * links.
 */
export function toInlineHtml(markdown: string): string | null {
  const text = (markdown ?? '').trim();
  if (text.includes('\n\n') || /^(#{1,6}\s|[-*+]\s|\d+[.)]\s|>|\|)/m.test(text)) return null;
  return md.renderInline(text.replace(/\n/g, ' '));
}

/**
 * What a table looks like on the way out of the editor, versus what GFM needs.
 *
 * TABLES WERE BEING STORED AS RAW HTML AND PUBLISHED AS VISIBLE MARKUP. The
 * editor's table extension writes `<table><colgroup>…<tbody><tr><th>…` with
 * every cell's text wrapped in a `<p>`, and turndown-plugin-gfm refuses both of
 * those shapes:
 *
 *   the heading row is only recognised as one when its `<tbody>` is the table's
 *   FIRST child, and a `<colgroup>` sits in front of it - so the table was
 *   judged to have no header, `tableShouldBeSkipped` returned true, and
 *   turndown fell back to keeping the element verbatim;
 *
 *   and a cell holding a `<p>` becomes a cell holding blank lines, which is not
 *   a GFM row at all - a pipe table is one line per row by definition.
 *
 * The consequence was invisible in the editor, which parses the stored HTML back
 * and shows a table again, and glaring in the export: `renderMarkdown` builds
 * markup itself and escapes everything else, so a real clinic article published
 * with `<table style="min-width: 100px;">` printed across the page as text.
 *
 * Both are removed here rather than worked around downstream, because the stored
 * Markdown is what the legal check, the section splitter and all four exports
 * read - and a pipe table is what all of them already understand.
 */
function flattenTables(html: string): string {
  return (
    html
      // Column widths are not expressible in GFM and are the reason the header
      // row goes unrecognised. See migration note above.
      .replace(/<colgroup>[\s\S]*?<\/colgroup>/gi, '')
      .replace(/<col\b[^>]*>/gi, '')
      // A cell's paragraphs become one line. Two paragraphs in one cell are rare
      // and become a break rather than silently running together.
      .replace(/<(t[hd])\b([^>]*)>([\s\S]*?)<\/\1>/gi, (_whole, tag: string, attrs: string, inner: string) => {
        const flat = inner
          .replace(/<\/p>\s*<p[^>]*>/gi, '<br>')
          .replace(/<\/?p[^>]*>/gi, '')
          .replace(/\s*\n\s*/g, ' ')
          .trim();
        return `<${tag}${attrs}>${flat}</${tag}>`;
      })
  );
}

/** The document, back to the Markdown everything else reads. */
export function toMarkdown(html: string): string {
  return (
    turndown
      .turndown(flattenTables(html))
      /*
       * Editors emit empty paragraphs where a person pressed Enter twice, and
       * turndown faithfully turns them into runs of blank lines. Three blank
       * lines mean nothing in Markdown and make every diff noisy.
       */
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

/**
 * Whether a round trip through the editor would change the stored text.
 *
 * Used to decide whether opening an article in the rich editor and closing it
 * without typing should mark it as edited. Conversions normalise - a setext
 * heading becomes ATX, `*` becomes `-` - and reporting that as the writer's
 * change would make "unsaved" meaningless.
 */
export const wouldRewrite = (markdown: string): boolean =>
  toMarkdown(toHtml(markdown)) !== markdown.trim();

/**
 * The drag payload an insertable tile carries.
 *
 * The image strip and the 差し込み tiles have always been `draggable` and have
 * always said 「タイルを本文にドラッグ」, and dropping one inserted the characters
 * `![カバー画像](/api/v1/images/…)` into the document as text - because the only
 * thing they put on the drag was `text/plain`, and text/plain dropped into a
 * document is text. The promise was in the UI and the handling was nowhere.
 *
 * Its own MIME type rather than sniffing text/plain: a writer dragging a line
 * out of another window is dragging text and must keep getting text, and the
 * two are indistinguishable once a URL is involved. text/plain is still set
 * alongside it, so dropping a tile into a plain textarea - the export note, a
 * search box - keeps working the way it does today.
 */
export const MARKDOWN_DRAG_MIME = 'application/x-kiji-markdown';
